"use client";

import React, {useEffect, useRef, useState} from "react";
import {useTheme} from "@mui/material/styles";
import {Box, Typography} from "@mui/material";
import {Form, Formik} from "formik";
import {usePathname, useRouter} from "next/navigation";
import {useScrollContext} from "@/context/scroll-context";
import LayoutSearchFilter from "@/components/layout/layout-search-filter";
import FaIcon from "@/components/utilities/fa-icon";
import SelectedChips from "@/app/(pages)/(general)/(home)/components/filter/selected-chips";
import FilterSelectGroup from "@/app/(pages)/(general)/(home)/components/filter/filter-select-group";
import {AdvanceFilterHomeProps, FormValues} from "@/app/(pages)/(general)/(home)/components/filter/types/filter";
import {PATHS} from "@/utils/paths";
import {LoadingButton} from "@mui/lab";


export default function SearchFilter({productGroups = [], dialog, handleClose}: AdvanceFilterHomeProps) {
    const theme = useTheme();
    const router = useRouter();
    const pathname = usePathname();
    const {scrollToSearch, setScrollToSearch} = useScrollContext();
    const [searchText, setSearchText] = useState("");
    const [showOverlay, setShowOverlay] = useState(false);
    const [shouldPulse, setShouldPulse] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const filterRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!scrollToSearch) return;

        const timeout = setTimeout(() => {
            const section = document.getElementById("advanced-filter-home");
            if (section) {
                window.scrollTo({top: section.offsetTop - 200, behavior: "smooth"});
                setShowOverlay(true);
                document.body.style.overflow = "hidden";
                document.body.style.overflowY = "scroll";

                const handleClickOutside = (event: MouseEvent) => {
                    const target = event.target as Element;
                    const isInsideSelect = target.closest('[class*="select"]');
                    if (filterRef.current && !filterRef.current.contains(target) && !isInsideSelect) {
                        setScrollToSearch(false);
                        setShowOverlay(false);
                        document.body.style.overflow = "auto";
                    }
                };

                document.addEventListener("mousedown", handleClickOutside);
                return () => document.removeEventListener("mousedown", handleClickOutside);
            }
        }, 300);

        return () => clearTimeout(timeout);
    }, [scrollToSearch, setScrollToSearch]);


    useEffect(() => {
        if (!showOverlay) return;

        let lastScrollY = window.scrollY;

        const handleWheel = (e: WheelEvent) => {
            e.preventDefault();
            setShowOverlay(false);
            setScrollToSearch(false);
        };

        const handleTouchMove = (e: TouchEvent) => {
            e.preventDefault();
            setShowOverlay(false);
            setScrollToSearch(false);
        };

        const handleScroll = () => {
            if (Math.abs(window.scrollY - lastScrollY) > 10) {
                setShowOverlay(false);
                setScrollToSearch(false);
            }
        };

        window.addEventListener("wheel", handleWheel, {passive: false});
        window.addEventListener("touchmove", handleTouchMove, {passive: false});
        window.addEventListener("scroll", handleScroll);

        return () => {
            window.removeEventListener("wheel", handleWheel);
            window.removeEventListener("touchmove", handleTouchMove);
            window.removeEventListener("scroll", handleScroll);
        };
    }, [showOverlay, setScrollToSearch]);

    const handleRedirect = (values: FormValues) => {
        setIsSubmitting(true);

        const buildIds = (arr: { value: string }[]) => arr.map(opt => opt.value).join(",");

        const params = new URLSearchParams();
        if (searchText) params.set("SearchText", searchText);
        if (values.Car?.length) params.set("Car", buildIds(values.Car));
        if (values.Company?.length) params.set("Company", buildIds(values.Company));
        if (values.Section?.length) params.set("Section", buildIds(values.Section));

        document.body.style.overflow = "auto";
        router.push(`${PATHS.GENERAL.PRODUCTS}?${params.toString()}`);
        handleClose?.();
    };

    const buildOptions = (level: number) =>
        productGroups.filter(pg => pg.pLevel === level).map(pg => ({
            label: pg.name,
            value: pg.id,
            image: pg.picture,
        }));

    const carOptions = buildOptions(1);
    const companyOptions = buildOptions(0);
    const sectionOptions = buildOptions(2);

    return (
        <>
            {showOverlay && (
                <Box
                    width="100vw"
                    height="100vh"
                    position="fixed"
                    top={0}
                    right={0}
                    zIndex={999}
                    bgcolor="rgba(0, 0, 0, 0.4)"
                />
            )}

            <Box
                id = "advanced-filter-home"
                position = "relative"
                width = "100%"
                ref = {filterRef}
                py={dialog ? 0 : 3}
                px= {{md: dialog ? 0 : 3, xs: dialog ? 0 : 3}}
                borderRadius={2}
                zIndex={1000}
                boxShadow={pathname === "/" ? theme.customShadows.card : undefined}
                bgcolor={pathname === "/" ? theme.palette.background.paper : "transparent"}
            >
                <Typography
                    display = "flex"
                    alignItems = "center"
                    variant = "h2"
                    fontWeight = {400}
                    color = {theme.palette.primary.main}
                    fontSize = {{md: 20, sm: 18, xs: 16}}
                    height = {{md: "auto", xs: 80}}
                    textAlign = {{md: "left", xs: "center"}}
                    flexDirection = {{md: "row", xs: "column"}}
                    gap = {1}
                >
                    <FaIcon fontSize = {24} color = {theme.palette.primary.main} faIcon = "fa-telescope" />
                    جست‌وجو براساس نام، کدفنی، نام خودرو، نام برند و بخش قطعه

                </Typography >

                <Formik
                    initialValues = {{Car: [], Company: [], Section: []}}
                    onSubmit = {handleRedirect}
                >
                    {({setFieldValue, values, submitForm}) => {

                        return (
                            <Form
                                onKeyDown = {(e) => {
                                    if (e.key !== "Enter") return;

                                    const el = e.target as HTMLElement;
                                    const inCombo =
                                        el.closest('[role="combobox"]') ||
                                        el.closest('.MuiAutocomplete-popper') ||
                                        el.closest('[role="listbox"]') ||
                                        el.getAttribute('contenteditable') === 'true';

                                    if (inCombo) return;

                                    e.preventDefault();
                                    submitForm();
                                    setShouldPulse(true);
                                    setTimeout(() => setShouldPulse(false), 400);
                                }}
                            >
                                <Box
                                    display = "flex"
                                    flexWrap = "wrap"
                                    flexDirection = {{xs: "column", sm: "row"}}
                                    gap = {1.5}
                                    mt = {3}
                                    height = {{md: 55, xs: "100%"}}
                                >
                                    <Box id="filter-search-text" flexGrow={6} width={{ xs: "100%", md: "auto" }}>
                                        <LayoutSearchFilter searchText={searchText} setSearchText={setSearchText} />
                                    </Box>

                                    <FilterSelectGroup
                                        values = {values}
                                        options = {{carOptions, companyOptions, sectionOptions}}
                                    />

                                    <LoadingButton
                                        id="filter-submit"
                                        loading = {isSubmitting}
                                        type = "submit"
                                        variant = "contained"
                                        size = "large"
                                        sx = {{
                                            mt: {sm: 0, xs: 1},
                                            minWidth: {xs: "auto", md: 55},
                                            height: {xs: 46, md: "100%"},
                                            transition: "transform 0.25s ease, background 0.25s ease",
                                            animation: shouldPulse ? "pulse 0.4s ease" : "none",
                                            /* '&:hover': {
                                                 transform: "scale(1.06)",
                                             },*/
                                        }}
                                    >

                                        <Typography
                                            fontWeight = {400}
                                            fontSize = {16}
                                            mr = {1}
                                            display = {{xs: "block", md: "none"}}
                                            whiteSpace = "nowrap"
                                        >
                                            پیدا کن
                                        </Typography >

                                        <FaIcon
                                            sx = {{display: {md: "block", xs: "none"}}}
                                            faIcon = "fa-search"
                                            fontSize = {26}
                                        />

                                        <FaIcon
                                            sx = {{display: {md: "none", xs: "block"}}}
                                            faIcon = "fa-search"
                                            fontSize = {18}
                                        />

                                    </LoadingButton >
                                </Box >

                                <SelectedChips values = {values} setFieldValue = {setFieldValue} dialog = {dialog} />

                            </Form >
                        );
                    }}
                </Formik >
            </Box >
        </>
    );
}
